import { Link } from 'react-router-dom';
import { useTranslation, useI18nStore } from '../lib/i18n';
import { useSystemSettings } from '../hooks/useSystemSettings';
import { Button } from '../components/ui/button';
import { Calendar, CheckCircle, Building2, ArrowRight, Shield, Users, Clock, Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const LANGUAGES = [
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' }
];

export const Landing = () => {
  const { t, language } = useTranslation();
  const { setLanguage } = useI18nStore();
  const { settings } = useSystemSettings();

  const currentLang = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];

  const features = [
    {
      icon: Calendar,
      title: t('landing.feature1Title'),
      description: t('landing.feature1Desc')
    },
    {
      icon: CheckCircle,
      title: t('landing.feature2Title'),
      description: t('landing.feature2Desc')
    },
    {
      icon: Building2,
      title: t('landing.feature3Title'),
      description: t('landing.feature3Desc')
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-800" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white dark:bg-slate-800/80 backdrop-blur-md border-b border-slate-100 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <Calendar className="h-8 w-8 text-slate-800 dark:text-slate-100" />
              <span className="font-bold text-xl text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {settings.siteName || 'OMS Portal'}
              </span>
            </div>
            <div className="flex items-center gap-4">
              {/* Language Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-2 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:text-slate-100">
                    <Globe className="h-4 w-4" />
                    <span>{currentLang.flag} {currentLang.code.toUpperCase()}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {LANGUAGES.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => setLanguage(lang.code)}
                      className={language === lang.code ? 'bg-slate-100 dark:bg-slate-700' : ''}
                    >
                      <span className="mr-2">{lang.flag}</span>
                      {lang.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Link to="/login">
                <Button variant="ghost" className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:text-slate-100" data-testid="nav-login-btn">
                  {t('common.login')}
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-slate-900 text-white hover:bg-slate-800" data-testid="nav-register-btn">
                  {t('common.register')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-slate-100 tracking-tight leading-tight"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {t('landing.heroTitle')}
              </h1>
              <p className="mt-6 text-lg text-slate-600 dark:text-slate-300 leading-relaxed max-w-lg">
                {t('landing.heroSubtitle')}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button 
                    size="lg" 
                    className="bg-slate-900 text-white hover:bg-slate-800 hover:shadow-lg transition-all px-8 py-6 text-lg"
                    data-testid="hero-get-started-btn"
                  >
                    {t('landing.getStarted')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="border-slate-300 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600 dark:bg-slate-700 px-8 py-6 text-lg"
                  data-testid="hero-learn-more-btn"
                >
                  {t('landing.learnMore')}
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={settings.landingImage}
                  alt="Modern office"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating stats card */}
              <div className="absolute -bottom-6 -left-6 bg-white dark:bg-slate-800 rounded-xl shadow-lg p-4 border border-slate-100 dark:border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-50 rounded-lg">
                    <Users className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">1,000+</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Active Users</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {t('landing.features')}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-slate-50 dark:bg-slate-800 rounded-2xl p-8 hover:bg-white dark:bg-slate-800 hover:shadow-lg hover:border-slate-200 dark:border-slate-600 border border-transparent transition-all duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="p-3 bg-slate-900 rounded-xl w-fit">
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 
                  className="mt-6 text-xl font-semibold text-slate-900 dark:text-slate-100"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {feature.title}
                </h3>
                <p className="mt-3 text-slate-600 dark:text-slate-300 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <p className="text-4xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>98%</p>
              <p className="mt-2 text-slate-400">Customer Satisfaction</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>50+</p>
              <p className="mt-2 text-slate-400">Companies Trust Us</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>24/7</p>
              <p className="mt-2 text-slate-400">Support Available</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-slate-800">
        <div className="max-w-3xl mx-auto text-center">
          <h2 
            className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Ready to streamline your leave management?
          </h2>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">
            Join thousands of companies already using OMS Portal to manage their workforce efficiently.
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              className="mt-8 bg-slate-900 text-white hover:bg-slate-800 hover:shadow-lg transition-all px-8 py-6 text-lg"
              data-testid="cta-register-btn"
            >
              Start Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800 border-t border-slate-100 dark:border-slate-700">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-6 w-6 text-slate-800 dark:text-slate-100" />
              <span className="font-bold text-lg text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {settings.siteName || 'OMS Portal'}
              </span>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              © 2024 {settings.siteName || 'OMS Portal'} Management. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
